import React from 'react'
import styled from 'styled-components'

import { defaultCTAURI } from 'src/modules/utils/constants'

import { createUrl } from 'src/modules/utils/createUrl'
import { useSelector } from 'react-redux'
import { selectCTAParams } from 'src/modules/reducers/params'
import CTAButton from 'src/components/common/CTAButtonNew'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import Image from '../Image'
import { useCtaText } from 'src/modules/hooks/useCtaText'
import {
  body,
  borderRadius,
  colors,
  heading,
  mediaQuery,
  spacing,
  typographyMixin,
} from 'src/constants/marketing-ds'
import useIsDesktop from 'src/modules/hooks/useIsDesktop'
import { SectionContainer } from '../SharedComponents'

const breakpoint = 981

const HighlightSection = ({
  ctaText,
  image,
  title,
  subtitle,
  miceText,
  background,
  lang = 'en',
  ctaHrefOverride,
  showHeadlineTop = false,
  disableFreeTrial,
  market,
  urlParams,
  savingsText,
}) => {
  const parsedParams = useSelector(selectCTAParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const isDesktop = useIsDesktop(breakpoint)
  let ctaURL = createUrl(defaultCTAURI, parsedParams)
  if (urlParams) {
    urlParams.forEach((param) => {
      const key = Object.keys(param)[0]
      const value = param[key]
      ctaURL = replaceUrlParam(ctaURL, key, value)
    })
  }
  ctaURL = replaceUrlParam(ctaURL, 'comp', 'feature')

  const isEs = lang === 'es'
  const ctaCopy = useCtaText({ lang, market, disableFreeTrial, ctaText })
  const headerTitle = title || (isEs ? 'Lo Mejor de la TV en vivo' : 'Enjoy Your Free Trial')
  const handleOnClick = (e) => {
    fireUIInteractionEvent(
      {
        element: ctaCopy,
        component: 'free_trial_section_cta_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <SectionContainer backgroundColor={background || colors.gradients.midnight.light}>
      <SectionWrapper className="highlight-section">
        {!isDesktop && showHeadlineTop && (
          <SectionContent isHeadlineTop={true}>
            {savingsText && <SavingsText>{savingsText}</SavingsText>}
            <SectionTitle>{headerTitle}</SectionTitle>
          </SectionContent>
        )}
        <SectionInner className="highlight-section-inner">
          <ImageWrapper className="highlight-section-image">
            {image ? (
              <Image src={image} alt="" />
            ) : (
              <Image
                src="https://assets-imgx.fubo.tv/marketing/lp/images/devices/LP_group_fubo-devices-min-v2.png"
                width="100%"
                alt=""
              />
            )}
          </ImageWrapper>
          <SectionContent className="highlight-section-content">
            {((showHeadlineTop && isDesktop) || !showHeadlineTop) && (
              <>
                {savingsText && <SavingsText>{savingsText}</SavingsText>}
                <SectionTitle>{headerTitle}</SectionTitle>
              </>
            )}
            <SectionParagraph>
              {subtitle ||
                (isEs
                  ? 'Deportes, entretenimiento y mucho más!'
                  : "Get to know everything we offer with a free trial. We'll email you when it's almost over.")}
            </SectionParagraph>
            <SectionCTA>
              <ButtonWrapper>
                <CTAButton link={ctaHrefOverride || ctaURL} onClick={handleOnClick}>
                  {ctaCopy}
                </CTAButton>
              </ButtonWrapper>
            </SectionCTA>
            {miceText && <div className="mice-text">{miceText}</div>}
          </SectionContent>
        </SectionInner>
      </SectionWrapper>
    </SectionContainer>
  )
}

export default HighlightSection

const SectionWrapper = styled.section`
  width: 100%;
  padding: ${spacing.xxxl} 0;

  @media (max-width: 981px) {
    padding: ${spacing.xl} 0;
  }

  .mice-text {
    color: #fff;
    font-size: ${typographyMixin(body.sm)};
    max-width: 800px;
    margin: 0 auto;
    text-align: center;

    a {
      text-decoration: underline;
    }

    @media (min-width: 982px) {
      margin: 0;
      text-align: left;
    }
  }
`

const SectionInner = styled.div`
  display: flex;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  align-items: center;
  gap: ${spacing.md};

  @media (max-width: 981px) {
    flex-direction: column;
  }
  ${mediaQuery.tablet} {
    gap: ${spacing.lg};
  }
  ${mediaQuery.desktop} {
    gap: ${spacing.xxxl};
  }
`

const ImageWrapper = styled.div`
  max-width: 540px;
  width: 80%;

  @media (min-width: 982px) {
    flex: 1 1 50%;
    max-width: 635px;
    width: 100%;
  }
  img {
    width: 100%;
  }
`

const SectionContent = styled.div`
  max-width: 550px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  justify-content: center;
  align-items: center;
  text-align: center;
  ${(props) =>
    props.isHeadlineTop &&
    `
      margin-bottom: ${spacing.md};
  `}

  @media (min-width: 982px) {
    flex: 1 1 50%;
    justify-content: flex-start;
    align-items: flex-start;
    text-align: left;
  }

  @media (max-width: 981px) {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }
`

const SectionTitle = styled.h2`
  ${typographyMixin(heading.md)}
  ${mediaQuery.tablet} {
    ${typographyMixin(heading.lg)}
  }
  ${mediaQuery.desktop} {
    ${typographyMixin(heading.xl)}
  }
`
const SectionParagraph = styled.p`
  margin: 0;
  ${typographyMixin(body.sm)}
  ${mediaQuery.tablet} {
    ${typographyMixin(body.lg)}
  }
`

const SectionCTA = styled.div``

const ButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`
const SavingsText = styled.p`
  margin: 0;
  ${typographyMixin(heading.xs)}
  color: ${colors.slate[10]};
  background: ${colors.mint[120]};
  padding: ${spacing.xxxs} ${spacing.xxs};
  border-radius: ${borderRadius.xs};
`
